import {
  WhmBox,
  WhmButton,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogContentText,
  WhmDialogTitle,
  WhmStack,
} from "@securitize/reactjs-whm";
import { useGetArchiveMutation } from "../../../../data/useProfile";
import { useCallback, useContext, useRef } from "react";
import { SnackbarContext } from "../../../shared/Snackbar";
import { CustomWhmLoadingButton } from "../../../shared/CustomWhmLoadingButton";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { translationKeys } from "../../../../contexts/I18nContext";
import { ReactComponent as DownloadDocuments } from "./DownloadDocuments.svg";

export default function DownloadModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const getArchiveMutation = useGetArchiveMutation();

  const canceled = useRef(false);

  const snackbar = useContext(SnackbarContext);

  const download = useCallback(() => {
    getArchiveMutation.mutate(undefined, {
      onSuccess: (data) => {
        if (canceled.current) {
          return;
        }

        const dataUrl = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = dataUrl;
        link.setAttribute("download", "securitize_data.zip");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(dataUrl);

        snackbar.showSnackbar({
          message: (
            <Translate
              translationKey={translationKeys.DOWNLOAD_DATA_POPUP_SUCCESS}
            />
          ),
          severity: "success",
        });
        onClose();
      },
      onError: (error) => {
        console.log("Error downloading user data:", error);
        snackbar.showSnackbar({
          message: (
            <TranslateHtml
              translationKey={translationKeys.DOWNLOAD_DATA_POPUP_ERROR}
            />
          ),
          severity: "error",
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate is stable
  }, [getArchiveMutation.mutate, snackbar]);

  return (
    <WhmDialog open={open} onClose={onClose} sx={{ maxWidth: "444px" }}>
      <WhmDialogTitle sx={{ fontSize: "1.25rem" }}>
        <Translate translationKey={translationKeys.DOWNLOAD_DATA_POPUP_TITLE} />
      </WhmDialogTitle>
      <WhmDialogContent>
        <WhmStack sx={{ alignItems: "center" }}>
          <WhmBox component={DownloadDocuments} />
          <WhmDialogContentText>
            <Translate
              translationKey={translationKeys.DOWNLOAD_DATA_POPUP_BODY}
            />
          </WhmDialogContentText>
        </WhmStack>
      </WhmDialogContent>
      <WhmDialogActions>
        <WhmButton
          onClick={() => {
            canceled.current = true;
            onClose();
          }}
        >
          <Translate
            translationKey={translationKeys.DOWNLOAD_DATA_POPUP_CANCEL}
          />
        </WhmButton>
        <CustomWhmLoadingButton
          variant="contained"
          color="primary"
          onClick={download}
          loading={getArchiveMutation.isPending}
        >
          {getArchiveMutation.isPending ? (
            <Translate
              translationKey={
                translationKeys.DOWNLOAD_DATA_POPUP_BUTTON_LOADING
              }
            />
          ) : (
            <Translate
              translationKey={translationKeys.DOWNLOAD_DATA_POPUP_BUTTON}
            />
          )}
        </CustomWhmLoadingButton>
      </WhmDialogActions>
    </WhmDialog>
  );
}
